import { toast } from "sonner";
import { useTranslation } from "@/features/utils/i18n";
import type { AvailableTool, ClientTool, Tool } from "../types";
import { useToolCatalog } from "./use-tool-catalog";
import {
  useCreateClientTool,
  useDeleteClientTool,
  useUpdateClientTool,
} from "./use-tools";

/**
 * What a tool's configuration form starts from: the tool's defaults, under
 * whatever the client saved. A tool that is not active yet is its defaults.
 */
export function toolConfiguration(
  tool: Pick<Tool, "default_configuration">,
  clientTool?: Pick<ClientTool, "configuration">,
): Record<string, unknown> {
  return clientTool
    ? { ...tool.default_configuration, ...clientTool.configuration }
    : { ...tool.default_configuration };
}

/**
 * Switching a module on and off, from the grid or from its own page.
 *
 * On creates the activation with the tool's untouched defaults — what « Activer
 * l'outil » used to send before anyone had edited the form. Off removes the
 * activation. Both report through a toast and resolve to whether they went
 * through; neither throws.
 */
export function useToolActivation() {
  const { t } = useTranslation("tools");
  const catalog = useToolCatalog();
  const createMutation = useCreateClientTool();
  const updateMutation = useUpdateClientTool();
  const deleteMutation = useDeleteClientTool();

  const activate = async ({
    tool,
    clientTool,
  }: AvailableTool): Promise<boolean> => {
    try {
      if (clientTool) {
        // A row switched off rather than deleted: turn it back on as it was.
        await updateMutation.mutateAsync({
          id: clientTool.id,
          data: { enabled: true },
        });
      } else {
        await createMutation.mutateAsync({
          tool_id: tool.id,
          enabled: true,
          configuration: toolConfiguration(tool),
        });
      }
      toast.success(
        t("tools.messages.activated", { toolLabel: catalog.label(tool) }),
      );
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("tools.messages.saveError"),
      );
      return false;
    }
  };

  const deactivate = async ({
    tool,
    clientTool,
  }: AvailableTool): Promise<boolean> => {
    if (!clientTool) return true;
    try {
      await deleteMutation.mutateAsync(clientTool.id);
      toast.success(
        t("tools.messages.deactivated", { toolLabel: catalog.label(tool) }),
      );
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("tools.messages.deactivateError"),
      );
      return false;
    }
  };

  return {
    activate,
    deactivate,
    isPending:
      createMutation.isPending ||
      updateMutation.isPending ||
      deleteMutation.isPending,
  };
}
